/*
 * RETAINED, CURRENTLY UNUSED — do not delete.
 *
 * This native data card was the original result treatment. The owner chose the
 * generated poster images instead (see ResultPosterTile.js), because the cards
 * reduced each student to a uniform statistic and showed their face too small.
 * The component is kept intact for a future restyle of the site, when a native
 * card may be wanted again — it is complete, tested, and still exports
 * `hasConsent`, which ResultVideoTile and StoriesPage both import from here.
 */

import React, { useState } from 'react';

/*
 * One student result card, teal/amber system (mirrors the landing page's
 * programs-card treatment). Presentation-only and prop-driven: a 4-card strip
 * of this same component goes on the landing page later.
 *
 * CONSENT GATE — enforced here, not only in the data file: an entry renders
 * only when consentOnFile is exactly true. These are minors' faces, full names
 * and scores; a carelessly added entry without consent must not render no
 * matter which surface mounts this component.
 *
 * Admission line: `outcome`, when present, is an explicit override (used for
 * multi-institution or no-institution outcomes) and wins outright; otherwise
 * it is composed from `institution` + `programme`. If neither yields
 * anything, no pill renders at all — a Class-10 board-result record has no
 * admission line to show.
 *
 * `metric` is a single headline figure (e.g. a percentile) shown when there
 * are no subject-level marks to carry the story. A `subjects` entry may omit
 * `score` when only the subject name is known — that row then renders the
 * name alone, no trailing mark.
 *
 * Missing photo (`photoSrc` null) or a photo that fails to load both fall
 * back to the same neutral initials avatar — never a broken-image icon.
 *
 * Card is `h-full`: both mount points (the rail's flex row, /results' CSS
 * grid) default to align-items: stretch, so a fixed-width wrapper/cell
 * already gets stretched to the row's tallest sibling — `h-full` is what
 * makes THIS card actually fill that stretched box, which is what lets
 * `grow` + `mt-auto` below pin the admission pill to a shared bottom edge
 * instead of every card floating at its own natural height.
 *
 * Name uses `overflow-wrap: anywhere`, not `break-words`: a name is never
 * allowed to clip past the card edge, and an unbroken long token (a long
 * surname, a `[PLACEHOLDER_SURNAME]` token) has no natural break point for
 * `break-word` to use — `anywhere` forces a break regardless.
 */

export const hasConsent = (entry) => entry?.consentOnFile === true;

const STRIPES_LIGHT =
  'repeating-linear-gradient(135deg, rgba(11,58,69,.05) 0 8px, rgba(11,58,69,.09) 8px 16px)';

const getInitials = (name = '') =>
  name
    .replace(/\[.*?\]/g, '') // strip a literal "[PLACEHOLDER_SURNAME]" token, not just its brackets
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const InitialsAvatar = ({ studentName }) => (
  <div
    className="w-12 h-12 rounded-full border border-brand-line shrink-0 flex items-center justify-center font-bold text-[13px] text-brand-deep"
    style={{ background: STRIPES_LIGHT }}
  >
    {getInitials(studentName)}
  </div>
);

const ResultCard = ({ entry }) => {
  const [imgFailed, setImgFailed] = useState(false);
  if (!hasConsent(entry)) return null;
  const {
    studentName,
    photoSrc,
    board,
    batchYear,
    subjects = [],
    metric,
    institution,
    programme,
    outcome,
  } = entry;

  const admissionLine = outcome || [institution, programme].filter(Boolean).join(' — ') || null;
  const metaLine = [board, batchYear].filter(Boolean).join(' · ');

  return (
    <div className="h-full bg-white border border-brand-line rounded-[14px] lg:rounded-2xl overflow-hidden flex flex-col">
      <div className="h-[5px] lg:h-1.5 bg-brand-amber" />
      <div className="p-[18px] lg:p-6 flex flex-col gap-3 lg:gap-4 grow">
        <div className="flex items-center gap-3">
          {photoSrc && !imgFailed ? (
            <img
              src={photoSrc}
              alt={studentName}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="w-12 h-12 rounded-full object-cover border border-brand-line shrink-0"
            />
          ) : (
            <InitialsAvatar studentName={studentName} />
          )}
          <div className="min-w-0">
            <span className="block text-[15px] lg:text-base font-bold leading-tight text-brand-deep [overflow-wrap:anywhere]">
              {studentName}
            </span>
            {metaLine && (
              <span className="block text-[11.5px] lg:text-xs text-brand-muted mt-0.5">
                {metaLine}
              </span>
            )}
          </div>
        </div>

        {metric && (
          <p className="text-[13px] lg:text-[13.5px] font-bold text-brand-deep bg-brand-tint rounded-lg px-3 py-2 text-center">
            {metric}
          </p>
        )}

        {subjects.length > 0 && (
          <div className="flex flex-col divide-y divide-brand-line/70">
            {subjects.map((subject) => (
              <div key={subject.name} className="flex items-center justify-between gap-3 py-1.5 lg:py-2">
                <span className="text-[13px] lg:text-[13.5px] text-brand-slate">{subject.name}</span>
                {subject.score && (
                  <span className="text-[13px] lg:text-[13.5px] font-bold text-brand-deep">
                    {subject.score}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {admissionLine && (
          <p className="mt-auto text-[12.5px] lg:text-[13px] font-semibold leading-snug text-brand-deep bg-brand-tint rounded-lg px-3 py-2">
            {admissionLine}
          </p>
        )}
      </div>
    </div>
  );
};

export default ResultCard;
